/**
 * What the admin pages say about a record, as pure functions of the record (docs/specs/
 * accounts.md, section 8). They are apart from the views so that a test holds each rule to account
 * without a DOM, and so that a word is chosen in one place.
 */
import type { AdminHealth, AdminRun, AdminUser, UserPatch } from './adminApi.ts';

// ---- users ----

export type UserState = 'active' | 'disabled' | 'awaiting setup';

/**
 * One state per user. `disabled` wins: a disabled user with no password is out of play, and the
 * missing password is not what stops them. A user with no password is `awaiting setup` whether or
 * not a link is live — `userStateNote` says which.
 */
export function userState(user: Pick<AdminUser, 'disabled' | 'has_password'>): UserState {
  if (user.disabled) return 'disabled';
  return user.has_password ? 'active' : 'awaiting setup';
}

/** The `.key` pair for a state (docs/design-system.md, "State key"). The word carries the state;
 * the colour only repeats it. */
export const USER_STATE_KEY: Record<UserState, string> = {
  active: 'k-present',
  'awaiting setup': 'k-partial',
  disabled: 'k-unknown',
};

/**
 * What an admin must do next, or null. A user who awaits setup and holds no live link is stuck:
 * the link expired, or nobody made one. Only a new link moves them.
 */
export function userStateNote(
  user: Pick<AdminUser, 'disabled' | 'has_password' | 'setup_link_outstanding'>,
): string | null {
  if (userState(user) !== 'awaiting setup') return null;
  return user.setup_link_outstanding ? 'a setup link is live' : 'no live setup link — make a new one';
}

/** The quota cell: runs used against the quota. An account with no quota says so in words. */
export function quotaCell(user: Pick<AdminUser, 'monthly_run_quota' | 'runs_used_this_month'>): string {
  if (user.monthly_run_quota == null) return 'no limit';
  return `${user.runs_used_this_month} of ${user.monthly_run_quota} this month`;
}

/**
 * The quota field of a form, as the server wants it: null for an empty field ("no limit"), a
 * whole number that is not negative, or a fault. The text is what the person typed.
 */
export function parseQuotaInput(text: string): { quota: number | null } | { fault: string } {
  const trimmed = text.trim();
  if (trimmed === '') return { quota: null };
  if (!/^\d+$/.test(trimmed)) return { fault: 'Enter a whole number, or leave the field empty for no limit.' };
  return { quota: Number(trimmed) };
}

/**
 * A row action that changes a user through PATCH. Each one is an ABSOLUTE intent, named after the
 * button's label, and its payload never depends on the browser's copy of the user. The page may
 * be stale: another admin may have disabled the user a minute ago. A payload derived from the
 * stale copy (`disabled: !user.disabled`) would then INVERT the other admin's change — a click on
 * "Disable" would enable. An absolute payload can at worst repeat a change.
 */
export type PatchAction = 'make-admin' | 'remove-admin' | 'disable' | 'enable';

export function patchFor(action: PatchAction): UserPatch {
  switch (action) {
    case 'make-admin':
      return { role: 'admin' };
    case 'remove-admin':
      return { role: 'user' };
    case 'disable':
      return { disabled: true };
    case 'enable':
      return { disabled: false };
  }
}

/** The role button and the state button that a row offers, from the copy of the user on screen.
 * The copy chooses the LABEL. The label, and nothing else, then chooses the payload. */
export function roleAction(user: Pick<AdminUser, 'role'>): PatchAction {
  return user.role === 'admin' ? 'remove-admin' : 'make-admin';
}

export function stateAction(user: Pick<AdminUser, 'disabled'>): PatchAction {
  return user.disabled ? 'enable' : 'disable';
}

/**
 * What the users page does with a user it has just created. With the list on screen, the user
 * joins it. Without the list — it is still loading, or the load failed — the page must not build
 * a list of one: a table that shows only the new user, under "1 users", is a false statement
 * about who exists. It reloads.
 */
export function afterCreate(
  list: { status: 'ready'; data: AdminUser[] } | { status: 'loading' | 'failed' },
  created: AdminUser,
): { kind: 'append'; users: AdminUser[] } | { kind: 'reload' } {
  if (list.status !== 'ready') return { kind: 'reload' };
  return { kind: 'append', users: [...list.data.filter((u) => u.id !== created.id), created] };
}

/** The setup link as the admin sends it: this site's origin, then the path the server returned. */
export function setupUrl(origin: string, setupPath: string): string {
  return `${origin.replace(/\/+$/, '')}${setupPath}`;
}

/** Spec section 4: a setup link is valid for 72 hours. */
export const SETUP_LINK_HOURS = 72;

// ---- runs ----

export const NO_OWNER = 'no owner';

export function ownerCell(run: Pick<AdminRun, 'owner_email'>): string {
  return run.owner_email ?? NO_OWNER;
}

/** An admin sees cost (spec section 5). It is data, so it has two decimals and a unit, always. */
export function costCell(run: Pick<AdminRun, 'cost_usd'>): string {
  return `$${run.cost_usd.toFixed(2)}`;
}

/** Where a row of the runs table goes: the EXISTING run route, never an admin copy of it. */
export function runHref(run: Pick<AdminRun, 'id' | 'session_id'>): string {
  return `#/sessions/${run.session_id}/runs/${run.id}`;
}

export function artifactHref(run: Pick<AdminRun, 'id' | 'session_id'>): string {
  return `${runHref(run)}/artifact`;
}

// ---- health ----

/**
 * Free disk against the launch floor. Below the floor the server starts no run, so that is `below
 * floor`. Within twice the floor is `warn`: the next few runs may use the rest. A volume the server
 * could not read is `not readable`, which is never the same claim as zero.
 */
export type DiskState = 'ok' | 'warn' | 'below floor' | 'not readable';

export const DISK_WARN_FACTOR = 2;

export function diskState(disk: AdminHealth['disk']): DiskState {
  if (disk.free_bytes == null) return 'not readable';
  if (disk.free_bytes < disk.min_free_bytes) return 'below floor';
  if (disk.free_bytes < disk.min_free_bytes * DISK_WARN_FACTOR) return 'warn';
  return 'ok';
}

export const DISK_STATE_KEY: Record<DiskState, string> = {
  ok: 'k-present',
  warn: 'k-partial',
  'below floor': 'k-absent',
  'not readable': 'k-unknown',
};

export function diskNote(state: DiskState): string | null {
  switch (state) {
    case 'warn':
      return `Free space is within ${DISK_WARN_FACTOR}× the launch floor. Free space with deploy/prune.sh before the floor is reached.`;
    case 'below floor':
      return 'Free space is below the launch floor, so the server starts no run. Free space with deploy/prune.sh.';
    case 'not readable':
      return 'The server could not read the data volume. A launch is not refused for that.';
    case 'ok':
      return null;
  }
}

const GIB = 1024 ** 3;

/** Bytes as GB with one decimal: the floor is set in whole GB, so this is the scale it is read at. */
export function formatBytes(bytes: number): string {
  return `${(bytes / GIB).toFixed(1)} GB`;
}
